import { ByteReader } from "../replay/bytes";
import type { ReplayFile } from "../replay/replay";
import { WorldState } from "./world";

/**
 * Plays a replay into a `WorldState`. Time is measured in frames (fractional
 * between frames): the state holds frame `state.frame`, and the display shows
 * `alpha` of the way from the previous frame to it.
 */
export class Playback {
  readonly state: WorldState;
  /** Display time in frames. */
  time = 0;
  playing = false;
  /** Game seconds per real second. */
  speed = 8;
  /** Waiting for a chunk to decompress. */
  buffering = false;
  loop = false;

  private reader: ByteReader | null = null;
  private chunkIndex = -1;
  private ready = new Map<number, Uint8Array>();
  private seekSeq = 0;
  private listeners: ((jump: boolean) => void)[] = [];

  constructor(readonly replay: ReplayFile) {
    this.state = new WorldState(replay.header, replay.statics);
    // Huge battles redraw more per frame: start slower.
    if (replay.header.unit_ids > 400_000) this.speed = 2;
  }

  get frames(): number {
    return this.replay.header.frames;
  }

  /** Frames per game second. */
  get frameRate(): number {
    const h = this.replay.header;
    return h.tick_rate / h.frame_interval;
  }

  /** How far (0..1) the display is between the previous frame and the current. */
  get alpha(): number {
    return Math.max(0, Math.min(1, this.time - (this.state.frame - 1)));
  }

  /** Called whenever new frames have been applied; `jump` when by seeking. */
  onFrame(f: (jump: boolean) => void): void {
    this.listeners.push(f);
  }

  private changed(jump: boolean): void {
    this.state.index();
    for (const f of this.listeners) f(jump);
  }

  private request(chunk: number): void {
    if (chunk < 0 || chunk >= this.replay.header.chunks.length || this.ready.has(chunk)) return;
    void this.replay.chunk(chunk).then((bytes) => {
      this.ready.set(chunk, bytes);
      // Keep only the chunks near the play head.
      for (const k of this.ready.keys()) if (Math.abs(k - this.chunkIndex) > 2) this.ready.delete(k);
    });
  }

  /** Jumps to `frame` (decoding from its chunk's keyframe when needed). */
  async seek(frame: number): Promise<void> {
    frame = Math.max(0, Math.min(this.frames - 1, Math.round(frame)));
    const seq = ++this.seekSeq;
    const chunk = this.replay.chunkOf(frame);
    const sameChunk = chunk === this.chunkIndex && this.reader !== null;
    if (!(sameChunk && frame >= this.state.frame)) {
      if (!this.ready.has(chunk)) this.buffering = true;
      const bytes = this.ready.get(chunk) ?? (await this.replay.chunk(chunk));
      this.buffering = false;
      if (seq !== this.seekSeq) return;
      this.ready.set(chunk, bytes);
      this.chunkIndex = chunk;
      this.reader = new ByteReader(bytes);
      this.state.frame = this.replay.header.chunks[chunk].first_frame - 1;
    }
    while (this.state.frame < frame) this.state.apply(this.reader!);
    this.state.settle();
    this.time = frame;
    this.request(chunk + 1);
    this.changed(true);
  }

  /** Advances playback by `dt` real seconds. */
  tick(dt: number): void {
    if (!this.playing || !this.reader) return;
    const last = this.frames - 1;
    this.time = Math.min(last, this.time + dt * this.speed * this.frameRate);
    let applied = false;
    this.buffering = false;
    while (this.time > this.state.frame && this.state.frame < last) {
      if (this.reader.done) {
        const next = this.chunkIndex + 1;
        const bytes = this.ready.get(next);
        if (!bytes) {
          this.request(next);
          this.buffering = true;
          this.time = this.state.frame;
          break;
        }
        this.chunkIndex = next;
        this.reader = new ByteReader(bytes);
        this.request(next + 1);
      }
      // Several frames in one display frame: only the last one's motion shows.
      this.state.apply(this.reader);
      applied = true;
    }
    if (applied) this.changed(false);
    if (this.time >= last && this.state.frame >= last) {
      if (this.loop) void this.seek(0);
      else this.playing = false;
    }
  }

  /** Steps one frame forward or back (pauses). */
  async step(delta: number): Promise<void> {
    this.playing = false;
    await this.seek(this.state.frame + delta);
  }
}
